/**
 * Финансовые расчеты для калькуляторов
 */

/**
 * Расчет ежемесячного платежа по аннуитетному кредиту
 * 
 * @param {number} loanAmount - Сумма кредита
 * @param {number} termMonths - Срок кредита в месяцах
 * @param {number} annualRate - Годовая процентная ставка (в процентах)
 * @returns {number} Ежемесячный платеж
 */
export const calculateAnnuityPayment = (loanAmount, termMonths, annualRate) => {
  const monthlyRate = annualRate / 12 / 100;
  
  if (monthlyRate === 0) {
    return loanAmount / termMonths;
  }
  
  const x = Math.pow(1 + monthlyRate, termMonths);
  return loanAmount * monthlyRate * x / (x - 1);
};

/**
 * Расчет ежемесячного платежа для дифференцированного кредита
 * 
 * @param {number} loanAmount - Сумма кредита
 * @param {number} termMonths - Срок кредита в месяцах
 * @param {number} annualRate - Годовая процентная ставка (в процентах)
 * @param {number} month - Номер месяца (начиная с 1)
 * @returns {number} Ежемесячный платеж
 */
export const calculateDifferentiatedPayment = (loanAmount, termMonths, annualRate, month) => {
  const monthlyRate = annualRate / 12 / 100;
  const principalPayment = loanAmount / termMonths;
  const remainingPrincipal = loanAmount - (principalPayment * (month - 1));
  const interestPayment = remainingPrincipal * monthlyRate;
  
  return principalPayment + interestPayment;
};

/**
 * Расчет графика платежей для аннуитетного кредита
 * 
 * @param {number} loanAmount - Сумма кредита
 * @param {number} termMonths - Срок кредита в месяцах
 * @param {number} annualRate - Годовая процентная ставка (в процентах)
 * @returns {Array} График платежей
 */
export const calculateAnnuitySchedule = (loanAmount, termMonths, annualRate) => {
  const monthlyRate = annualRate / 12 / 100;
  const monthlyPayment = calculateAnnuityPayment(loanAmount, termMonths, annualRate);
  
  let remainingPrincipal = loanAmount;
  const schedule = [];
  
  for (let month = 1; month <= termMonths; month++) {
    const interestPayment = remainingPrincipal * monthlyRate;
    const principalPayment = monthlyPayment - interestPayment;
    remainingPrincipal -= principalPayment;
    
    schedule.push({
      month,
      payment: monthlyPayment,
      principalPayment,
      interestPayment,
      remainingPrincipal: Math.max(0, remainingPrincipal),
    });
  }
  
  return schedule;
};

/**
 * Расчет графика платежей для дифференцированного кредита
 * 
 * @param {number} loanAmount - Сумма кредита
 * @param {number} termMonths - Срок кредита в месяцах
 * @param {number} annualRate - Годовая процентная ставка (в процентах)
 * @returns {Array} График платежей
 */
export const calculateDifferentiatedSchedule = (loanAmount, termMonths, annualRate) => {
  const monthlyRate = annualRate / 12 / 100;
  const principalPayment = loanAmount / termMonths;
  
  let remainingPrincipal = loanAmount;
  const schedule = [];
  
  for (let month = 1; month <= termMonths; month++) {
    const interestPayment = remainingPrincipal * monthlyRate;
    const payment = principalPayment + interestPayment;
    remainingPrincipal -= principalPayment;
    
    schedule.push({
      month,
      payment,
      principalPayment,
      interestPayment,
      remainingPrincipal: Math.max(0, remainingPrincipal),
    });
  }
  
  return schedule;
};

/**
 * Расчет общей суммы процентов по кредиту
 * 
 * @param {Array} schedule - График платежей
 * @returns {number} Сумма процентов
 */
export const calculateTotalInterest = (schedule) => {
  return schedule.reduce((sum, payment) => sum + payment.interestPayment, 0);
};

/**
 * Расчет доходности депозита с учетом капитализации
 * 
 * @param {number} initialAmount - Начальная сумма вклада
 * @param {number} termMonths - Срок вклада в месяцах
 * @param {number} annualRate - Годовая процентная ставка (в процентах)
 * @param {string} capitalization - Тип капитализации ('none', 'monthly', 'quarterly', 'annually')
 * @param {Array} additionalPayments - Массив дополнительных взносов {month, amount}
 * @param {Array} withdrawals - Массив снятий {month, amount}
 * @returns {Object} Результаты расчета
 */
export const calculateDeposit = (
  initialAmount, 
  termMonths, 
  annualRate, 
  capitalization = 'none',
  additionalPayments = [],
  withdrawals = []
) => {
  let balance = initialAmount;
  const monthlyRate = annualRate / 12 / 100;
  const schedule = [];
  let totalInterest = 0;
  
  const capitalizationPeriods = {
    none: 0,
    monthly: 1,
    quarterly: 3,
    annually: 12
  };
  
  const period = capitalizationPeriods[capitalization] || 0;
  
  for (let month = 1; month <= termMonths; month++) {
    // Дополнительные взносы
    const payments = additionalPayments
      .filter(payment => payment.month === month)
      .reduce((sum, payment) => sum + payment.amount, 0);
    
    balance += payments;
    
    // Снятия
    const withdrawal = withdrawals
      .filter(w => w.month === month)
      .reduce((sum, w) => sum + w.amount, 0);
    
    balance -= withdrawal;
    
    // Расчет процентов
    let interest = balance * monthlyRate;
    totalInterest += interest;
    
    // Капитализация
    if (period > 0 && month % period === 0) {
      balance += interest;
      interest = 0;
    }
    
    schedule.push({
      month,
      payments,
      withdrawal,
      interest,
      balance
    });
  }
  
  // Если без капитализации, добавляем проценты в конце срока
  if (period === 0) {
    balance += totalInterest;
  }
  
  return {
    finalAmount: balance,
    totalInterest,
    schedule
  };
};

/**
 * Расчет НДС
 * 
 * @param {number} amount - Сумма
 * @param {number} vatRate - Ставка НДС (в процентах)
 * @param {boolean} includesVat - Включает ли исходная сумма НДС
 * @returns {Object} Результаты расчета
 */
export const calculateVAT = (amount, vatRate, includesVat = false) => {
  const rate = vatRate / 100;
  
  if (includesVat) {
    // Выделение НДС из суммы
    const withoutVAT = amount / (1 + rate);
    const vatAmount = amount - withoutVAT;
    
    return {
      amountWithVAT: amount,
      amountWithoutVAT: withoutVAT,
      vatAmount
    };
  } else {
    // Начисление НДС на сумму
    const vatAmount = amount * rate;
    const withVAT = amount + vatAmount;
    
    return {
      amountWithVAT: withVAT,
      amountWithoutVAT: amount,
      vatAmount
    };
  }
};

/**
 * Конвертация валют
 * 
 * @param {number} amount - Сумма для конвертации
 * @param {number} rate - Обменный курс
 * @returns {number} Сконвертированная сумма
 */
export const convertCurrency = (amount, rate) => {
  return amount * rate;
};

/**
 * Конвертация процентов
 * 
 * @param {number|string} value - Значение для конвертации
 * @param {string} fromType - Тип исходного значения ('percent', 'decimal', 'fraction')
 * @param {string} toType - Тип результата ('percent', 'decimal', 'fraction')
 * @returns {number|string} Сконвертированное значение
 */
export const convertPercentage = (value, fromType, toType) => {
  let decimal;
  
  // Парсинг входного значения
  if (typeof value === 'string') {
    value = value.trim().replace(',', '.');
  }
  
  // Преобразование входного значения в десятичную дробь
  switch (fromType) {
    case 'percent':
      decimal = Number(value) / 100;
      break;
    case 'decimal':
      decimal = Number(value);
      break;
    case 'fraction':
      const parts = String(value).split('/');
      if (parts.length === 2) {
        decimal = Number(parts[0]) / Number(parts[1]);
      } else {
        decimal = Number(value);
      }
      break;
    default:
      throw new Error(`Неподдерживаемый тип конвертации: ${fromType}`);
  }
  
  // Проверка корректности числа
  if (isNaN(decimal)) {
    throw new Error('Некорректное значение для конвертации');
  }
  
  // Преобразование десятичной дроби в требуемый формат
  switch (toType) {
    case 'percent':
      return Number((decimal * 100).toFixed(4));
    case 'decimal':
      return Number(decimal.toFixed(4));
    case 'fraction':
      // Конвертация в обыкновенную дробь с попыткой упрощения
      return convertToFraction(decimal);
    default:
      throw new Error(`Неподдерживаемый тип конвертации: ${toType}`);
  }
};

/**
 * Преобразование десятичной дроби в обыкновенную дробь
 * 
 * @param {number} decimal - Десятичная дробь для конвертации
 * @returns {string} Обыкновенная дробь
 */
const convertToFraction = (decimal) => {
  // Максимальный знаменатель для упрощения дроби
  const MAX_DENOMINATOR = 1000;
  
  // Функция поиска наибольшего общего делителя
  const gcd = (a, b) => b ? gcd(b, a % b) : a;
  
  // Попытка найти простую дробь
  for (let denominator = 2; denominator <= MAX_DENOMINATOR; denominator++) {
    const numerator = Math.round(decimal * denominator);
    const simplifiedGCD = gcd(numerator, denominator);
    
    const simplifiedNumerator = numerator / simplifiedGCD;
    const simplifiedDenominator = denominator / simplifiedGCD;
    
    // Проверяем точность преобразования
    const reconstructedDecimal = simplifiedNumerator / simplifiedDenominator;
    if (Math.abs(reconstructedDecimal - decimal) < 0.0001) {
      return `${simplifiedNumerator}/${simplifiedDenominator}`;
    }
  }
  
  // Если не удалось найти простую дробь, возвращаем приближенное значение
  return decimal.toFixed(4);
};


